import { useAsync } from '@tiger/hooks';
import { Button } from 'antd';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import BottomBox from 'components/BottomBox';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import { TaxLimitInfo } from 'modules/Deposit/type';
import security from 'stores/security';
import API from 'utils/API';
import { formatMoney } from 'utils/format';
import http from 'utils/http';
import { getMaskText } from 'utils/util';
import './style.scss';

interface ConfirmProps {
    isIRA?: boolean;
}

const Confirm: FC<ConfirmProps> = observer(({ isIRA }) => {
    const location = useLocation();
    const locationState = location.state as {
        selectedBank: PlaidBankCardItem;
        params: {
            account_id: string;
            amount: string;
            currency: string;
            otp_code?: string;
            verify_token?: string;
        };
        tax: TaxLimitInfo;
    } | null;
    const isManualBank = locationState?.selectedBank?.id_source === 'Bank'; // 手动认证的银行账户
    const navigate = useNavigate();
    const [{ loading, data }] = useAsync(async () => {
        if (security.isAccountOpened) {
            try {
                const { instantAmount, intransitAmount } = await http
                    .get<{
                        instantAmount: number;
                        intransitAmount: number;
                    }>(API.TRADE.predict(), { params: { amount: locationState?.params.amount } })
                    .then(({ data }) => data);

                return {
                    instantAmount,
                    intransitAmount
                };
            } catch (error) {
                Toast.show(error);
            }
        }

        return Promise.resolve();
    });

    if (!locationState) {
        return <Navigate to=".." replace />;
    }

    const goNext = () => {
        const { amount } = locationState.params;

        navigate('../complete', {
            state: {
                type: 'success',
                source: 'deposit',
                amount: formatMoney(Number(amount).toFixed(2), '$'),
                tax: locationState.tax,
                isManualBank
            }
        });
    };

    const submit = async () => {
        Toast.loading(true);

        const { account_id, amount } = locationState.params;
        const { id_source, id } = locationState.selectedBank;
        const taxYear = isIRA ? locationState.tax.year : undefined;

        try {
            if (security.optStatus) {
                await http.post(
                    API.TRADE.funding.depositAch(),
                    {
                        ...locationState.params,
                        customer_id: security.currentCustomerId,
                        ach_card_id: id,
                        source: id_source,
                        tax_year: taxYear
                    },
                    {
                        params: {
                            customer_id: security.currentCustomerId,
                            account_id: security.currentAccountId
                        }
                    }
                );

                goNext();
            } else {
                const submitParams = {
                    account_id,
                    amount,
                    currency: 'USD',
                    ach_card_id: id,
                    source: id_source,
                    customer_id: security.currentCustomerId,
                    tax_year: taxYear
                };

                await http.post(API.TRADE.funding.submitDeposit(), submitParams, {
                    params: {
                        customer_id: security.currentCustomerId,
                        account_id: security.currentAccountId
                    }
                });

                goNext();
            }
        } catch (error) {
            await Toast.show(error);

            // code: 11084 超时，回到第一步
            if (error.error_code * 1 === 11084) {
                navigate('..');
            }
        }

        Toast.loading(false);
    };

    const { isAccountOpened } = security;
    const { mask, account, institution_id, institution_name, subtype } = locationState.selectedBank;
    const currAccount = institution_id === 'ins_56' ? getMaskText(mask) || account : account;

    return (
        <div className="deposit-ach-confirm-root">
            <ScrollTop />
            <h3 className="main-title">{__('Confirm your payment information')}</h3>
            <div className="info">
                <p className="info-item">
                    <span className="label">{__('Bank Name')}</span>
                    <span className="value">{`${institution_name.toUpperCase()}`}</span>
                </p>
                <p className="info-item">
                    <span className="label">{__('Bank Account')}</span>
                    <span className="value">{`${subtype?.toUpperCase()} - ${currAccount}`}</span>
                </p>
                <p className="info-item">
                    <span className="label">{isIRA ? __('Contribution Amount') : __('Deposit Amount')}</span>
                    <span className="value">{formatMoney(Number(locationState.params.amount).toFixed(2), '$')}</span>
                </p>
                {!isManualBank && isAccountOpened && (
                    <div className="instant-info">
                        <div className="info-item">
                            <div className="label">
                                <span>{isIRA ? __('Instant funding') : __('Instant Funds')}</span>
                            </div>
                            <span className="value">
                                {typeof data?.instantAmount === 'number'
                                    ? formatMoney(Number(data.instantAmount).toFixed(2), '$')
                                    : '- -'}
                            </span>
                        </div>
                        <div className="tips">{__('(the fund you can trade immediately)')}</div>
                        {!isIRA && (
                            <>
                                <div className="info-item">
                                    <div className="label">
                                        <span>{isIRA ? __('Contribution amount') : __('Additional Funds')}</span>
                                    </div>
                                    <span className="value">
                                        {typeof data?.instantAmount === 'number'
                                            ? formatMoney(Number(data.intransitAmount).toFixed(2), '$')
                                            : '- -'}
                                    </span>
                                </div>
                                <div className="tips mb-0">{__('(up to 5 business days to clear)')}</div>
                            </>
                        )}
                    </div>
                )}
                {isIRA && (
                    <>
                        <p className="info-item">
                            <span className="label">{__('Tax Year')}</span>
                            <span className="value">{locationState.tax.year}</span>
                        </p>
                        <p className="info-item">
                            <span className="label">{__('Remaining Contribution Limit')}</span>
                            <span className="value">
                                {formatMoney(locationState.tax.limit - Number(locationState.params.amount) || 0, '$')}
                            </span>
                        </p>
                    </>
                )}
            </div>
            <ol className="confirm-tips">
                {isManualBank ? (
                    <li>{__('TradeUP charges a $30 fee for each ACH deposit that is returned.')}</li>
                ) : (
                    <>
                        <li>{__('Instant Funds cannot be used for IPO subscription or options trading.')}</li>
                        <li>{__('TradeUP charges a $30 fee for each ACH deposit that is returned.')}</li>
                    </>
                )}
            </ol>
            <BottomBox className="btn-box">
                <Button
                    className="btn-important"
                    type="primary"
                    block
                    loading={loading}
                    disabled={loading}
                    onClick={submit}>
                    {__('Confirm')}
                </Button>
            </BottomBox>
        </div>
    );
});

export default Confirm;
